import { find, orderBy } from 'lodash';
import { getNullSelectOption, SelectOptions } from '../../../../shared/form/inputs/Select';
import { CylinderSizingLimitLookup } from '../heat-pump/cylinderSizingLookups';

export const cylinderVolumes = ['150', '200', '250', '300', '400', '400T', '500', '600', '800', '1000'] as const;

export const parseCylinderVolume = (cylinderVolume: string): number => {
  return parseInt(cylinderVolume.replace('T', ''));
};

const cylinderVolumeNumbers = cylinderVolumes.map(cylinderVolume => parseCylinderVolume(cylinderVolume));

type ValidCylinderVolume = typeof cylinderVolumeNumbers[number];

export const cylinderVolumeSelectOptions: SelectOptions<string | null> = [
  getNullSelectOption<string>(),
  ...cylinderVolumes.map(cylinderVolume => ({ value: cylinderVolume, text: cylinderVolume.toString() })),
];

export const cylinderVolumeSelectOptionsWithLimits = (
  limits: CylinderSizingLimitLookup,
  isStandard: boolean,
): SelectOptions<string | null> =>
  cylinderVolumeSelectOptions.filter(
    option =>
      option.value === null ||
      (isStandard &&
        parseCylinderVolume(option.value) >= (limits.standardLowerLimit ?? 0) &&
        parseCylinderVolume(option.value) <= (limits.standardUpperLimit ?? 1000)) ||
      (!isStandard &&
        parseCylinderVolume(option.value) >= (limits.solarLowerLimit ?? 0) &&
        parseCylinderVolume(option.value) <= (limits.solarUpperLimit ?? 1000)),
  );

type CylinderVolumeDefaults = {
  numberOfBedrooms: number;
  numberOfBathrooms: number;
  cylinderVolumeInLitres: {
    ifNoSolarOrHeatPump: ValidCylinderVolume;
    ifSolarButNoHeatPump: ValidCylinderVolume;
    ifHeatPumpButNoSolar: ValidCylinderVolume;
    ifSolarAndHeatPump: ValidCylinderVolume;
  };
};

export const getDefaultCylinderVolumeOrNull = (
  numberOfBedrooms: number,
  numberOfBathrooms: number,
  hasHeatPump: boolean,
  hasSolar: boolean,
): ValidCylinderVolume | null => {
  if (numberOfBedrooms < 1 || numberOfBathrooms < 1 || numberOfBathrooms > numberOfBedrooms) {
    return null;
  }

  if (numberOfBedrooms > 8) {
    return defaultCylinderVolumeInLitresForGreaterThanEightBedrooms;
  }

  const matchesByNumberOfBedrooms = orderBy(
    cylinderVolumeDefaults.filter(d => d.numberOfBedrooms === numberOfBedrooms),
    d => d.numberOfBathrooms,
  );

  const matchOrNull = find(matchesByNumberOfBedrooms, d => d.numberOfBathrooms === numberOfBathrooms);

  const closestMatch = matchOrNull || matchesByNumberOfBedrooms[0];

  if (hasHeatPump) {
    if (hasSolar) {
      return closestMatch.cylinderVolumeInLitres.ifSolarAndHeatPump;
    } else {
      return closestMatch.cylinderVolumeInLitres.ifHeatPumpButNoSolar;
    }
  } else {
    if (hasSolar) {
      return closestMatch.cylinderVolumeInLitres.ifSolarButNoHeatPump;
    } else {
      return closestMatch.cylinderVolumeInLitres.ifNoSolarOrHeatPump;
    }
  }
};

export const cylinderVolumeDefaults: Array<CylinderVolumeDefaults> = [
  {
    numberOfBedrooms: 1,
    numberOfBathrooms: 1,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 150,
      ifSolarButNoHeatPump: 300,
      ifHeatPumpButNoSolar: 200,
      ifSolarAndHeatPump: 250,
    },
  },
  {
    numberOfBedrooms: 2,
    numberOfBathrooms: 1,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 150,
      ifSolarButNoHeatPump: 300,
      ifHeatPumpButNoSolar: 200,
      ifSolarAndHeatPump: 300,
    },
  },
  {
    numberOfBedrooms: 2,
    numberOfBathrooms: 2,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 200,
      ifSolarButNoHeatPump: 300,
      ifHeatPumpButNoSolar: 200,
      ifSolarAndHeatPump: 300,
    },
  },
  {
    numberOfBedrooms: 3,
    numberOfBathrooms: 1,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 200,
      ifSolarButNoHeatPump: 300,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 400,
    },
  },
  {
    numberOfBedrooms: 3,
    numberOfBathrooms: 2,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 200,
      ifSolarButNoHeatPump: 400,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 400,
    },
  },
  {
    numberOfBedrooms: 3,
    numberOfBathrooms: 3,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 300,
      ifSolarButNoHeatPump: 400,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 400,
    },
  },
  {
    numberOfBedrooms: 4,
    numberOfBathrooms: 1,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 300,
      ifSolarButNoHeatPump: 400,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 4,
    numberOfBathrooms: 2,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 400,
      ifSolarButNoHeatPump: 500,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 4,
    numberOfBathrooms: 3,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 500,
      ifSolarButNoHeatPump: 500,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 4,
    numberOfBathrooms: 4,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 500,
      ifSolarButNoHeatPump: 500,
      ifHeatPumpButNoSolar: 300,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 5,
    numberOfBathrooms: 3,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 500,
      ifSolarButNoHeatPump: 600,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 5,
    numberOfBathrooms: 4,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 500,
      ifSolarButNoHeatPump: 600,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 5,
    numberOfBathrooms: 5,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 500,
      ifSolarButNoHeatPump: 600,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 500,
    },
  },
  {
    numberOfBedrooms: 6,
    numberOfBathrooms: 3,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 600,
    },
  },
  {
    numberOfBedrooms: 6,
    numberOfBathrooms: 4,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 600,
    },
  },
  {
    numberOfBedrooms: 6,
    numberOfBathrooms: 5,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 600,
    },
  },
  {
    numberOfBedrooms: 6,
    numberOfBathrooms: 6,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 400,
      ifSolarAndHeatPump: 600,
    },
  },
  {
    numberOfBedrooms: 7,
    numberOfBathrooms: 3,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 7,
    numberOfBathrooms: 4,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 7,
    numberOfBathrooms: 5,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 600,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 7,
    numberOfBathrooms: 6,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 7,
    numberOfBathrooms: 7,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 800,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 8,
    numberOfBathrooms: 4,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 1000,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 8,
    numberOfBathrooms: 5,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 1000,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 8,
    numberOfBathrooms: 6,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 1000,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 8,
    numberOfBathrooms: 7,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 1000,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
  {
    numberOfBedrooms: 8,
    numberOfBathrooms: 8,
    cylinderVolumeInLitres: {
      ifNoSolarOrHeatPump: 800,
      ifSolarButNoHeatPump: 1000,
      ifHeatPumpButNoSolar: 500,
      ifSolarAndHeatPump: 800,
    },
  },
];

const defaultCylinderVolumeInLitresForGreaterThanEightBedrooms: ValidCylinderVolume = 1000;
